import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import { inviteUsersToTask } from '../../features/taskApplications/taskApplicationSlice';
import api from '../../utils/axiosConfig';
import { formatLogoUrl } from '../../utils/imageUtils';
import {
    ArrowLeftIcon,
    ClipboardDocumentListIcon,
    BuildingOfficeIcon,
    CurrencyDollarIcon,
    UserGroupIcon,
    ChartBarIcon,
    BanknotesIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    PencilIcon,
    PlayIcon,
    PauseIcon,
    UserPlusIcon,
    XMarkIcon,
    CheckCircleIcon,
    UserIcon
} from '@heroicons/react/24/outline';

interface BudgetScenario {
    id: string;
    name: string;
    description: string;
    range: {
        min: number;
        max: number;
    };
    budgetDistribution: {
        teamExecutive: number;
        teamLeader: number;
        districtHead: number;
        stateHead: number;
        regionalHead: number;
        admin: number;
    };
    isActive: boolean;
    createdAt: string;
}

interface TaskDetails {
    _id: string;
    title: string;
    description: string;
    taskUrl: string;
    isOnlineTask?: boolean;
    company: {
        _id: string;
        companyName: string;
        logo?: string;
    };
    postedBy: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    totalBudget: number;
    budgetScenarios: BudgetScenario[];
    currentScenario: string | null;
    taskCompletionMetrics: {
        totalTasksAssigned: number;
        completedTasks: number;
        completionPercentage: number;
        revenueGenerated: number;
        lastUpdated: string;
    };
    regions: string[];
    states: string[];
    districts: string[];
    targetDesignations: string[];
    requiredSkills: Array<{
        _id: string;
        name: string;
    }>;
    status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    startDate: string;
    endDate: string;
    validationRules: {
        requiredFields: string[];
    };
    assignedUsers: Array<{
        user?: {
            _id: string;
            firstName: string;
            lastName: string;
            email: string;
            designation: string;
            region: string;
            personalDetails: {
                address: {
                    state: string;
                    district: string;
                };
            };
            skill: {
                _id: string;
                name: string;
            };
        };
        staff?: {
            _id: string;
            firstName: string;
            lastName: string;
            email: string;
            role: string;
            designation?: string;
            isActive: boolean;
        };
        designation: string;
        referralLink?: string;
        myReferralLink?: string;
        assignedAt: string;
        status: 'assigned' | 'in_progress' | 'completed' | 'rejected';
        completedAt?: string;
        earnings?: number;
        dataSubmitted?: any[];
    }>;
    createdAt: string;
    updatedAt: string;
}

const TaskDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);


    const [task, setTask] = useState<TaskDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    // Switch to overview tab if data tab is active but user shouldn't see it
    useEffect(() => {
        if (activeTab === 'data' && user?.role === 'recruiter') {
            setActiveTab('overview');
        }
        // Switch to overview tab if applications tab is active but BDM shouldn't see it
        if (activeTab === 'applications' && user?.role === 'bdm') {
            setActiveTab('overview');
        }
    }, [activeTab, user?.role]);
    const [teamHierarchy, setTeamHierarchy] = useState<any>(null);
    const [earningsData, setEarningsData] = useState<any>(null);
    const [applications, setApplications] = useState<any[]>([]);
    const [applicationsLoading, setApplicationsLoading] = useState(false);
    const [applicationStatistics, setApplicationStatistics] = useState({
        totalApplications: 0,
        totalApplied: 0,
        totalApproved: 0,
        totalRejected: 0,
        totalInvited: 0
    });

    // Invite users state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [availableUsers, setAvailableUsers] = useState<any[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // CSV validation state
    const [showCsvValidationModal, setShowCsvValidationModal] = useState(false);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvData, setCsvData] = useState<any[]>([]);
    const [csvColumns, setCsvColumns] = useState<string[]>([]);
    const [columnMapping, setColumnMapping] = useState<{ [key: string]: string }>({});
    const [validationLoading, setValidationLoading] = useState(false);

    const fetchTeamHierarchy = useCallback(async (taskData?: TaskDetails) => {
        try {
            // This would be a new API endpoint to get team hierarchy
            const response = await api.get(`/tasks/${id}/hierarchy`);
            if (response.data.success) {
                setTeamHierarchy(response.data.data);
            }
        } catch (error) {
            // Error handled silently
        }
    }, [id]);

    const fetchEarningsData = useCallback(async (taskData: TaskDetails) => {
        try {
            // This would be a new API endpoint to get earnings data
            const response = await api.get(`/tasks/${id}/earnings`);
            if (response.data.success) {
                setEarningsData(response.data.data);
            }
        } catch (error) {
            // Error handled silently
        }
    }, [id]);

    const fetchApplications = useCallback(async () => {
        if (!id) return;

        try {
            setApplicationsLoading(true);
            const response = await api.get('/admin/task-applications', {
                params: {
                    taskId: id,
                    limit: 10
                }
            });

            if (response.data.success) {
                setApplications(response.data.data.applications || []);
                if (response.data.data.statistics) {
                    setApplicationStatistics({
                        totalApplications: response.data.data.statistics.totalApplications || 0,
                        totalApplied: response.data.data.statistics.totalApplied || 0,
                        totalApproved: response.data.data.statistics.totalApproved || 0,
                        totalRejected: response.data.data.statistics.totalRejected || 0,
                        totalInvited: response.data.data.statistics.totalInvited || 0
                    });
                }
            }
        } catch (error) {
            // Error handled silently
        } finally {
            setApplicationsLoading(false);
        }
    }, [id]);

    const fetchTaskDetails = useCallback(async () => {
        try {
            const response = await api.get(`/tasks/${id}`);
            if (response.data.success) {
                setTask(response.data.data);
                await fetchTeamHierarchy();
                await fetchEarningsData(response.data.data);
                await fetchApplications();
            }
        } catch (error: any) {
            dispatch(showToast({
                message: 'Failed to fetch task details',
                type: 'error'
            }));
            navigate('/admin/tasks');
        } finally {
            setLoading(false);
        }
    }, [id, dispatch, navigate, fetchTeamHierarchy, fetchEarningsData, fetchApplications]);

    useEffect(() => {
        if (id) {
            fetchTaskDetails();
        }
    }, [id, fetchTaskDetails]);

    const handleStatusChange = async (newStatus: string) => {
        if (!task) return;

        try {
            const response = await api.put(`/tasks/${task._id}`, { status: newStatus });
            if (response.data.success) {
                setTask(prev => prev ? { ...prev, status: newStatus as any } : null);
                dispatch(showToast({
                    message: `Task ${newStatus} successfully`,
                    type: 'success'
                }));
            }
        } catch (error: any) {
            dispatch(showToast({
                message: error?.response?.data?.message || 'Failed to update task status',
                type: 'error'
            }));
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200';
            case 'paused': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200';
            case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200';
            case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200';
            case 'draft': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200';
            case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200';
            case 'low': return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
        }
    };

    // Budget Scenario Helper Functions
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // Helper function to filter assignments based on user hierarchy
    const filterAssignmentsByHierarchy = useCallback((assignments: any[]) => {
        return assignments.filter(assignment => {
            const assignee = assignment.user || assignment.staff;
            const assigneeDesignation = assignee?.designation || (assignment.staff ? (assignee as any)?.role : undefined);

            // If current user is admin or company, show all data
            if (user?.role === 'admin' || user?.role === 'company') {
                return (assignment.dataSubmitted?.length || 0) > 0;
            }

            // For staff users, check if they should see this data based on hierarchy
            if (user?.role && ['recruiter', 'bdm', 'project_trainer', 'crm'].includes(user.role)) {
                // Staff users see all data (they have admin-like access)
                return (assignment.dataSubmitted?.length || 0) > 0;
            }

            // For regular users, apply hierarchical filtering
            const currentUserDesignation = (user as any)?.designation;
            const currentUserRegion = (user as any)?.region;
            const currentUserAddress = (user as any)?.personalDetails?.address;

            if (!currentUserDesignation || !assigneeDesignation) {
                return (assignment.dataSubmitted?.length || 0) > 0;
            }

            // Team executives only see their own data
            if (currentUserDesignation === 'team_executive') {
                return assignee?._id === (user as any)?._id && (assignment.dataSubmitted?.length || 0) > 0;
            }

            // Team leaders see data from team executives in their district
            if (currentUserDesignation === 'team_leader') {
                if (assigneeDesignation === 'team_executive') {
                    return (assignee as any)?.personalDetails?.address?.district === currentUserAddress?.district &&
                        (assignee as any)?.personalDetails?.address?.state === currentUserAddress?.state &&
                        (assignment.dataSubmitted?.length || 0) > 0;
                }
                return false;
            }

            // District heads see data from team executives and team leaders in their district
            if (currentUserDesignation === 'district_head') {
                if (assigneeDesignation === 'team_executive' || assigneeDesignation === 'team_leader') {
                    return (assignee as any)?.personalDetails?.address?.district === currentUserAddress?.district &&
                        (assignee as any)?.personalDetails?.address?.state === currentUserAddress?.state &&
                        (assignment.dataSubmitted?.length || 0) > 0;
                }
                return false;
            }

            // State heads see data from team executives, team leaders, and district heads in their state
            if (currentUserDesignation === 'state_head') {
                if (['team_executive', 'team_leader', 'district_head'].includes(assigneeDesignation)) {
                    return (assignee as any)?.personalDetails?.address?.state === currentUserAddress?.state &&
                        (assignee as any)?.region === currentUserRegion &&
                        (assignment.dataSubmitted?.length || 0) > 0;
                }
                return false;
            }

            // Regional heads see data from all users in their region
            if (currentUserDesignation === 'regional_head') {
                return (assignee as any)?.region === currentUserRegion && (assignment.dataSubmitted?.length || 0) > 0;
            }

            // Default: show all data
            return (assignment.dataSubmitted?.length || 0) > 0;
        });
    }, [user]);

    // Calculate earnings based on budget scenarios and user data
    const calculateEarnings = useCallback((task: TaskDetails, userAssignment: any) => {
        if (!task || !userAssignment || !task.budgetScenarios) {
            return { totalEarned: 0, validatedEarnings: 0, pendingEarnings: 0 };
        }

        const dataSubmitted = userAssignment.dataSubmitted || [];
        const currentDataCount = dataSubmitted.length;

        let totalEarnings = 0;
        let validatedEarnings = 0;
        let pendingEarnings = 0;

        // Map designation to budget distribution key
        const designationMap: { [key: string]: string } = {
            'team_executive': 'teamExecutive',
            'team_leader': 'teamLeader',
            'district_head': 'districtHead',
            'state_head': 'stateHead',
            'regional_head': 'regionalHead',
            'admin': 'admin'
        };

        const budgetKey = designationMap[userAssignment.designation] || userAssignment.designation;

        // Calculate earnings for each data entry based on budget scenarios and validation status
        for (let i = 0; i < dataSubmitted.length; i++) {
            const dataEntry = dataSubmitted[i];
            const dataIndex = i + 1; // 1-based index

            // Find the appropriate budget scenario for this data index
            const applicableScenario = task.budgetScenarios.find((scenario: any) =>
                scenario.isActive &&
                dataIndex >= scenario.range.min &&
                dataIndex <= scenario.range.max
            );

            if (applicableScenario) {
                const budgetDistribution = applicableScenario.budgetDistribution;
                const earningsPerEntry = (budgetDistribution as any)[budgetKey] || 0;

                totalEarnings += earningsPerEntry;

                // Check if data is validated/approved
                if (dataEntry.isValidated === true) {
                    validatedEarnings += earningsPerEntry;
                } else {
                    pendingEarnings += earningsPerEntry;
                }
            }
        }

        return {
            totalEarned: totalEarnings,
            validatedEarnings: validatedEarnings,
            pendingEarnings: pendingEarnings
        };
    }, []);

    // CSV validation functions
    const handleCsvFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            dispatch(showToast({
                message: 'Please select a CSV file',
                type: 'error'
            }));
            return;
        }

        setCsvFile(file);
        parseCsvFile(file);
    };

    const parseCsvFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const lines = text.split('\n').filter(line => line.trim());

            if (lines.length < 2) {
                dispatch(showToast({
                    message: 'CSV file must have at least a header and one data row',
                    type: 'error'
                }));
                return;
            }

            const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
            const data = lines.slice(1).map(line => {
                const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
                const row: any = {};
                headers.forEach((header, index) => {
                    row[header] = values[index] || '';
                });
                return row;
            });

            setCsvColumns(headers);
            setCsvData(data);

            // Auto-map columns based on task requirements
            const requiredFields = task?.validationRules?.requiredFields || [];
            const autoMapping: { [key: string]: string } = {};

            headers.forEach(header => {
                const matchingField = requiredFields.find(field =>
                    field.toLowerCase() === header.toLowerCase() ||
                    field.toLowerCase().includes(header.toLowerCase()) ||
                    header.toLowerCase().includes(field.toLowerCase())
                );
                if (matchingField) {
                    autoMapping[header] = matchingField;
                }
            });

            setColumnMapping(autoMapping);
        };
        reader.readAsText(file);
    };

    const handleColumnMapping = (csvColumn: string, taskField: string) => {
        setColumnMapping(prev => ({
            ...prev,
            [csvColumn]: taskField
        }));
    };

    const validateData = async () => {
        if (!task || !csvData.length) return;

        setValidationLoading(true);
        try {
            const requiredFields = task.validationRules?.requiredFields || [];
            const mappedFields = Object.values(columnMapping);

            // Check if all required fields are mapped
            const unmappedFields = requiredFields.filter(field => !mappedFields.includes(field));
            if (unmappedFields.length > 0) {
                dispatch(showToast({
                    message: `Please map all required fields: ${unmappedFields.join(', ')}`,
                    type: 'error'
                }));
                return;
            }

            // Find matching data entries
            const matches: { userAssignment: any, dataEntry: any, csvRow: any }[] = [];

            task.assignedUsers.forEach(assignment => {
                assignment.dataSubmitted?.forEach((dataEntry: any) => {
                    csvData.forEach(csvRow => {
                        let isMatch = true;

                        // Check if all mapped fields match
                        Object.entries(columnMapping).forEach(([csvColumn, taskField]) => {
                            const csvValue = csvRow[csvColumn]?.toString().toLowerCase().trim();
                            const dataValue = dataEntry.data?.[taskField]?.toString().toLowerCase().trim();

                            if (csvValue !== dataValue) {
                                isMatch = false;
                            }
                        });

                        if (isMatch) {
                            matches.push({ userAssignment: assignment, dataEntry, csvRow });
                        }
                    });
                });
            });

            if (matches.length === 0) {
                dispatch(showToast({
                    message: 'No matching data entries found',
                    type: 'warning'
                }));
                return;
            }

            // Update data entries to validated status
            const updatePromises = matches.map(async ({ userAssignment, dataEntry }) => {
                const response = await api.put(`/tasks/${task._id}/validate-data`, {
                    userId: userAssignment.user._id,
                    dataEntryId: dataEntry._id,
                    isValidated: true
                });
                return response.data;
            });

            await Promise.all(updatePromises);

            dispatch(showToast({
                message: `Successfully validated ${matches.length} data entries`,
                type: 'success'
            }));

            // Refresh task data
            await fetchTaskDetails();
            setShowCsvValidationModal(false);
            setCsvFile(null);
            setCsvData([]);
            setCsvColumns([]);
            setColumnMapping({});

        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to validate data',
                type: 'error'
            }));
        } finally {
            setValidationLoading(false);
        }
    };

    const getCurrentBudgetDistribution = () => {
        if (!task) return null;

        const currentTaskCount = task.taskCompletionMetrics?.completedTasks || 0;

        // Find the active scenario that matches the current task count
        const activeScenario = task.budgetScenarios?.find(scenario =>
            scenario.isActive &&
            currentTaskCount >= scenario.range.min &&
            currentTaskCount <= scenario.range.max
        );

        if (activeScenario) {
            return activeScenario.budgetDistribution;
        }

        // If no scenario matches, return the first active scenario as fallback
        const firstActiveScenario = task.budgetScenarios?.find(scenario => scenario.isActive);
        if (firstActiveScenario) {
            return firstActiveScenario.budgetDistribution;
        }
        return {
            teamExecutive: 0,
            teamLeader: 0,
            districtHead: 0,
            stateHead: 0,
            regionalHead: 0,
            admin: 0
        };
    };

    // Calculate hierarchical earnings based on budget scenarios and location matching
    const calculateHierarchicalEarnings = () => {
        if (!task) return {};

        const earnings: { [key: string]: number } = {};

        // Find all team executives who have submitted data
        const teamExecutivesWithData = task.assignedUsers.filter(assignment => {
            const assignee = assignment.user || assignment.staff;
            const designation = assignee?.designation || (assignment.staff ? (assignee as any)?.role : undefined);
            return designation === 'team_executive' && (assignment.dataSubmitted?.length || 0) > 0;
        });

        // Calculate earnings for each user based on budget scenarios
        task.assignedUsers.forEach(assignment => {
            const assignee = assignment.user || assignment.staff;
            const userId = assignee?._id;
            const designation = assignee?.designation || (assignment.staff ? (assignee as any)?.role : undefined);
            const userDataCount = assignment.dataSubmitted?.length || 0;

            let totalEarnings = 0;

            if (designation === 'team_executive') {
                // Team executives get earnings for their own data using budget scenarios
                totalEarnings = calculateEarnings(task, assignment).totalEarned;
            } else if (assignment.user) {
                // Only calculate hierarchical earnings for users (not staff)
                // Find team executives in the same location hierarchy
                const applicableTeamExecutives = teamExecutivesWithData.filter(teAssignment => {
                    const teUser = teAssignment.user;
                    if (!teUser || !assignee) return false;

                    // Location matching based on designation
                    if (designation === 'team_leader' || designation === 'district_head') {
                        // Match district + state
                        return teUser.personalDetails?.address?.district === (assignee as any).personalDetails?.address?.district &&
                            teUser.personalDetails?.address?.state === (assignee as any).personalDetails?.address?.state;
                    } else if (designation === 'state_head') {
                        // Match state + region
                        return teUser.personalDetails?.address?.state === (assignee as any).personalDetails?.address?.state &&
                            teUser.region === (assignee as any).region;
                    } else if (designation === 'regional_head') {
                        // Match region only
                        return teUser.region === (assignee as any).region;
                    } else if (designation === 'admin') {
                        // Admin gets earnings from all team executives (no location matching)
                        return true;
                    }
                    return false;
                });

                // Calculate earnings based on team executives' data submissions
                applicableTeamExecutives.forEach(teAssignment => {
                    const teDataCount = teAssignment.dataSubmitted?.length || 0;
                    if (teDataCount > 0) {
                        // Calculate earnings for each data entry using budget scenarios
                        for (let i = 0; i < teDataCount; i++) {
                            const dataIndex = i + 1; // 1-based index

                            // Find the appropriate budget scenario for this data index
                            const applicableScenario = task.budgetScenarios?.find((scenario: any) =>
                                scenario.isActive &&
                                dataIndex >= scenario.range.min &&
                                dataIndex <= scenario.range.max
                            );

                            if (applicableScenario) {
                                const budgetDistribution = applicableScenario.budgetDistribution;
                                const designationMap: { [key: string]: string } = {
                                    'team_leader': 'teamLeader',
                                    'district_head': 'districtHead',
                                    'state_head': 'stateHead',
                                    'regional_head': 'regionalHead',
                                    'admin': 'admin'
                                };
                                const budgetKey = designationMap[designation] || designation;
                                const earningsPerEntry = (budgetDistribution as any)[budgetKey] || 0;
                                totalEarnings += earningsPerEntry;
                            }
                        }
                    }
                });
            } else {
                // For staff, just calculate basic earnings based on their role
                const staffEarningsMap: { [key: string]: number } = {
                    'bdm': 0,
                    'project_trainer': 0,
                    'recruiter': 0,
                    'crm': 0
                };
                totalEarnings = userDataCount * (staffEarningsMap[designation] || 0);
            }

            if (userId) {
                earnings[userId] = totalEarnings;
            }
        });

        return earnings;
    };


    const toggleBudgetScenario = async (scenarioId: string) => {
        if (!task) return;

        try {
            // Toggle the scenario status locally first
            const updatedTask = {
                ...task,
                budgetScenarios: task.budgetScenarios.map(scenario =>
                    scenario.id === scenarioId
                        ? { ...scenario, isActive: !scenario.isActive }
                        : scenario
                )
            };
            setTask(updatedTask);

            // Update on server
            const response = await api.put(`/tasks/${task._id}/budget-scenarios`, {
                scenarioId,
                updateData: { isActive: !task.budgetScenarios.find(s => s.id === scenarioId)?.isActive }
            });

            if (response.data.success) {
                dispatch(showToast({
                    message: `Budget scenario ${updatedTask.budgetScenarios.find(s => s.id === scenarioId)?.isActive ? 'activated' : 'deactivated'} successfully`,
                    type: 'success'
                }));
            }
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to toggle budget scenario',
                type: 'error'
            }));
            // Revert local changes on error
            window.location.reload();
        }
    };

    // Invite users functions
    const fetchAvailableUsers = useCallback(async (taskId: string) => {
        if (!task) return;

        setUsersLoading(true);
        try {
            const response = await api.get(`/users/available-for-task/${taskId}`);

            if (response.data.success) {
                setAvailableUsers(response.data.data);
            }
        } catch (error) {
            dispatch(showToast({
                message: 'Failed to fetch available users',
                type: 'error'
            }));
        } finally {
            setUsersLoading(false);
        }
    }, [task, dispatch]);

    const handleInviteUsers = async () => {
        if (!task) return;

        if (selectedUsers.length === 0) {
            dispatch(showToast({
                message: 'Please select at least one user to invite',
                type: 'error'
            }));
            return;
        }

        try {
            await dispatch(inviteUsersToTask({
                taskId: task._id,
                userIds: selectedUsers
            })).unwrap();

            dispatch(showToast({
                message: 'Users invited successfully! They will receive notifications to apply.',
                type: 'success'
            }));

            setShowInviteModal(false);
            setSelectedUsers([]);
            fetchTaskDetails();
            fetchApplications();
        } catch (error: any) {
            dispatch(showToast({
                message: error.message || 'Failed to invite users',
                type: 'error'
            }));
        }
    };

    const handleInviteAllUsers = async () => {
        if (!task) return;

        try {
            await dispatch(inviteUsersToTask({
                taskId: task._id,
                userIds: [] // Empty array triggers auto-invite all matching users
            })).unwrap();

            dispatch(showToast({
                message: 'All matching users invited successfully! They will receive notifications to apply.',
                type: 'success'
            }));

            setShowInviteModal(false);
            setSelectedUsers([]);
            fetchTaskDetails();
            fetchApplications();
        } catch (error: any) {
            dispatch(showToast({
                message: error.message || 'Failed to invite users',
                type: 'error'
            }));
        }
    };

    const openInviteModal = () => {
        if (task) {
            setShowInviteModal(true);
            fetchAvailableUsers(task._id);
        }
    };


    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading task details...</p>
                </div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Task Not Found</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">The task you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/admin/tasks')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Tasks
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/admin/tasks')}
                        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeftIcon className="h-5 w-5 mr-2" />
                        Back to Tasks
                    </button>

                    <div className="flex items-start justify-between">
                        <div className="flex items-start">
                            <ClipboardDocumentListIcon className="h-10 w-10 text-blue-600 mr-4 mt-1" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{task.title}</h1>
                                <div className="flex items-center space-x-4">
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(task.status)}`}>
                                        {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(task.priority)}`}>
                                        {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => navigate(`/admin/tasks/${task._id}/edit`)}
                                className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                title="Edit Task"
                            >
                                <PencilIcon className="h-5 w-5" />
                            </button>

                            <button
                                onClick={openInviteModal}
                                className="p-2 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                title="Invite Users"
                            >
                                <UserPlusIcon className="h-5 w-5" />
                            </button>

                            {task.status === 'active' ? (
                                <button
                                    onClick={() => handleStatusChange('paused')}
                                    className="p-2 text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
                                    title="Pause Task"
                                >
                                    <PauseIcon className="h-5 w-5" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => handleStatusChange('active')}
                                    className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                    title="Activate Task"
                                >
                                    <PlayIcon className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-8">
                    <nav className="flex space-x-8">
                        {[
                            { id: 'overview', label: 'Overview', icon: EyeIcon },
                            { id: 'requirements', label: 'Requirements', icon: ClipboardDocumentListIcon },
                            { id: 'team', label: 'Team Hierarchy', icon: UserGroupIcon },
                            // { id: 'earnings', label: 'Earnings', icon: BanknotesIcon },
                            ...(user?.role !== 'recruiter' ? [{ id: 'data', label: 'Data Collection', icon: ChartBarIcon }] : []),
                            ...(user?.role !== 'bdm' ? [{ id: 'applications', label: 'Applications', icon: UserGroupIcon }] : [])
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === tab.id
                                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                            >
                                <tab.icon className="h-4 w-4 mr-2" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
                    {activeTab === 'overview' && (
                        <div className="p-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Basic Information */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                        <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-2" />
                                        Basic Information
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">{task.description}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Task URL</label>
                                            <a
                                                href={task.taskUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                            >
                                                {task.taskUrl}
                                            </a>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company</label>
                                            <div className="mt-1 flex items-center">
                                                {task.company.logo && (
                                                    <img
                                                        src={formatLogoUrl(task.company.logo)}
                                                        alt={task.company.companyName}
                                                        className="h-6 w-6 rounded mr-2"
                                                    />
                                                )}
                                                <span className="text-gray-900 dark:text-white">{task.company.companyName}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Posted By</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">
                                                {task.postedBy.firstName} {task.postedBy.lastName}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Budget & Timeline */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                        <CurrencyDollarIcon className="h-5 w-5 text-green-600 mr-2" />
                                        Budget & Timeline
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Total Budget</label>
                                            <p className="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">
                                                ₹{(task.totalBudget || 0).toLocaleString()}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">
                                                {new Date(task.startDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">
                                                {new Date(task.endDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Created</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">
                                                {new Date(task.createdAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'requirements' && (
                        <div className="p-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Skills & Designations */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                        <ClipboardDocumentListIcon className="h-5 w-5 text-blue-600 mr-2" />
                                        Required Skills & Designations
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Required Skills</label>
                                            <div className="flex flex-wrap gap-2">
                                                {task.requiredSkills.map((skill) => (
                                                    <span key={skill._id} className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full text-sm">
                                                        {skill.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target Designations</label>
                                            <div className="flex flex-wrap gap-2">
                                                {task.targetDesignations.map((designation) => (
                                                    <span key={designation} className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full text-sm">
                                                        {designation.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Budget Distribution */}
                            <div className="mt-8">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                        <CurrencyDollarIcon className="h-5 w-5 text-green-600 mr-2" />
                                        Budget Distribution
                                    </h3>
                                </div>

                                {/* Current Budget Distribution */}
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">
                                            Current Distribution
                                        </h4>
                                        {(() => {
                                            const currentTaskCount = task.taskCompletionMetrics?.completedTasks || 0;
                                            const currentScenario = task.budgetScenarios?.find(scenario =>
                                                scenario.isActive &&
                                                currentTaskCount >= scenario.range.min &&
                                                currentTaskCount <= scenario.range.max
                                            );
                                            return currentScenario && (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                                                    {currentScenario.name} Active ({currentTaskCount} tasks)
                                                </span>
                                            );
                                        })()}
                                    </div>

                                    {/* Total Budget Display */}
                                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <div className="flex justify-between items-center">
                                            <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">Total Budget:</span>
                                            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                                {formatCurrency(task.totalBudget || 0)}
                                            </span>
                                        </div>
                                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                                            Active Scenarios: {task.budgetScenarios?.filter(s => s.isActive).length || 0} |
                                            Total Scenarios: {task.budgetScenarios?.length || 0}
                                        </div>
                                    </div>

                                    {/* Budget Distribution Grid */}
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                        {(() => {
                                            let budgetDist = getCurrentBudgetDistribution() || {
                                                teamExecutive: 0,
                                                teamLeader: 0,
                                                districtHead: 0,
                                                stateHead: 0,
                                                regionalHead: 0,
                                                admin: 0
                                            };
                                            const entries = Object.entries(budgetDist);

                                            // If no budget distribution exists, create a default one
                                            if (entries.length === 0 && task.totalBudget > 0) {
                                                const totalBudget = task.totalBudget;
                                                budgetDist = {
                                                    teamExecutive: Math.floor(totalBudget * 0.4), // 40%
                                                    teamLeader: Math.floor(totalBudget * 0.25), // 25%
                                                    districtHead: Math.floor(totalBudget * 0.15), // 15%
                                                    stateHead: Math.floor(totalBudget * 0.1), // 10%
                                                    regionalHead: Math.floor(totalBudget * 0.05), // 5%
                                                    admin: Math.floor(totalBudget * 0.05) // 5%
                                                };
                                            }

                                            const finalEntries = Object.entries(budgetDist);

                                            if (finalEntries.length === 0) {
                                                return (
                                                    <div className="col-span-full text-center py-8">
                                                        <p className="text-gray-500 dark:text-gray-400">No budget distribution data available</p>
                                                        <p className="text-sm text-gray-400 mt-2">Total Budget: {formatCurrency(task.totalBudget || 0)}</p>
                                                    </div>
                                                );
                                            }

                                            return finalEntries.map(([role, amount]) => (
                                                <div key={role} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                                                        {role.replace(/([A-Z])/g, ' $1').trim()}
                                                    </p>
                                                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                                        {formatCurrency(amount as number)}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        {task.totalBudget ? `${(((amount as number) / task.totalBudget) * 100).toFixed(1)}%` : '0%'}
                                                    </p>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>

                                {/* Task Completion Metrics */}
                                {task.taskCompletionMetrics && (
                                    <div className="mb-6">
                                        <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">
                                            Completion Metrics
                                        </h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
                                                <p className="text-sm text-blue-600 dark:text-blue-400">Tasks Assigned</p>
                                                <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                                                    {task.taskCompletionMetrics.totalTasksAssigned}
                                                </p>
                                            </div>
                                            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                                                <p className="text-sm text-green-600 dark:text-green-400">Completed</p>
                                                <p className="text-lg font-semibold text-green-900 dark:text-green-100">
                                                    {task.taskCompletionMetrics.completedTasks}
                                                </p>
                                            </div>
                                            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
                                                <p className="text-sm text-purple-600 dark:text-purple-400">Completion %</p>
                                                <p className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                                                    {task.taskCompletionMetrics.completionPercentage}%
                                                </p>
                                            </div>
                                            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 text-center">
                                                <p className="text-sm text-yellow-600 dark:text-yellow-400">Revenue</p>
                                                <p className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">
                                                    {formatCurrency(task.taskCompletionMetrics.revenueGenerated)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Budget Scenarios */}
                                {task.budgetScenarios && task.budgetScenarios.length > 0 && (
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">
                                                Budget Scenarios
                                            </h4>
                                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                                Active: {task.budgetScenarios.filter(s => s.isActive).length} / {task.budgetScenarios.length}
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            {task.budgetScenarios.map((scenario) => (
                                                <div key={scenario.id} className={`p-4 rounded-xl border ${scenario.isActive
                                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                                                    }`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div>
                                                            <h5 className="font-semibold text-gray-900 dark:text-white">{scenario.name}</h5>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">{scenario.description}</p>
                                                            <div className="mt-1">
                                                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                    Range: {scenario.range?.min || 0} - {scenario.range?.max || 0} tasks
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <button
                                                                onClick={() => toggleBudgetScenario(scenario.id)}
                                                                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${scenario.isActive
                                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                                    : 'bg-gray-400 text-white hover:bg-gray-500'
                                                                    }`}
                                                            >
                                                                {scenario.isActive ? 'Active' : 'Inactive'}
                                                            </button>
                                                            {scenario.isActive && (
                                                                <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-xs font-medium">
                                                                    ACTIVE
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                                        {Object.entries(scenario.budgetDistribution).map(([role, amount]) => (
                                                            <div key={role} className="bg-white dark:bg-gray-600 rounded-lg p-3 text-center">
                                                                <p className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                                                                    {role.replace(/([A-Z])/g, ' $1').trim()}
                                                                </p>
                                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                    {formatCurrency(amount)}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Location Requirements */}
                            <div className="mt-8">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <BuildingOfficeIcon className="h-5 w-5 text-purple-600 mr-2" />
                                    Location Requirements
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target Regions</label>
                                        <div className="flex flex-wrap gap-2">
                                            {task.regions.map((region) => (
                                                <span key={region} className="px-3 py-1 bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200 rounded-full text-sm">
                                                    {region.charAt(0).toUpperCase() + region.slice(1)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target States</label>
                                        <div className="flex flex-wrap gap-2">
                                            {task.states.map((state) => (
                                                <span key={state} className="px-3 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200 rounded-full text-sm">
                                                    {state}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target Districts</label>
                                        <div className="flex flex-wrap gap-2">
                                            {task.districts.map((district) => (
                                                <span key={district} className="px-3 py-1 bg-pink-100 text-pink-800 dark:bg-pink-800 dark:text-pink-200 rounded-full text-sm">
                                                    {district}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'team' && (
                        <div className="p-8">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                    <UserGroupIcon className="h-5 w-5 text-blue-600 mr-2" />
                                    Team Hierarchy & Assignments
                                </h3>
                                <button
                                    onClick={() => fetchTeamHierarchy()}
                                    disabled={loading}
                                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </div>

                            {teamHierarchy ? (
                                <div className="space-y-6">
                                    {/* Task Summary */}
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                                        <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">{teamHierarchy.task.title}</h4>
                                        <p className="text-blue-700 dark:text-blue-300 mb-2">Company: {teamHierarchy.task.company}</p>
                                        <p className="text-blue-700 dark:text-blue-300">Total Budget: ₹{(task?.totalBudget || 0).toLocaleString()}</p>
                                    </div>

                                    {/* Regions Hierarchy */}
                                    {Object.keys(teamHierarchy.regions).length > 0 ? (
                                        <div className="space-y-4">
                                            {Object.entries(teamHierarchy.regions).map(([regionName, regionData]: [string, any]) => (
                                                <div key={regionName} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                                    <h5 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                                        <span className="h-3 w-3 bg-purple-500 rounded-full mr-2"></span>
                                                        {regionName} Region
                                                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                                            ({regionData.totalUsers || 0} users)
                                                        </span>
                                                    </h5>

                                                    {Object.keys(regionData.states).length > 0 && (
                                                        <div className="ml-6 space-y-3">
                                                            {Object.entries(regionData.states).map(([stateName, stateData]: [string, any]) => (
                                                                <div key={stateName} className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                                                    <h6 className="text-md font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                                                                        <span className="h-2 w-2 bg-indigo-500 rounded-full mr-2"></span>
                                                                        {stateName} State
                                                                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                                                            ({stateData.totalUsers || 0} users)
                                                                        </span>
                                                                    </h6>

                                                                    {Object.keys(stateData.districts).length > 0 && (
                                                                        <div className="ml-6 space-y-2">
                                                                            {Object.entries(stateData.districts).map(([districtName, districtData]: [string, any]) => (
                                                                                <div key={districtName} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                                                                    <h6 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                                                                                        <span className="h-2 w-2 bg-pink-500 rounded-full mr-2"></span>
                                                                                        {districtName} District
                                                                                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                                                                            ({districtData.totalUsers || 0} users)
                                                                                        </span>
                                                                                    </h6>

                                                                                    {districtData.users.length > 0 && (
                                                                                        <div className="ml-4 space-y-1">
                                                                                            {districtData.users.map((user: any) => {
                                                                                                const hierarchicalEarnings = calculateHierarchicalEarnings();
                                                                                                const userEarnings = hierarchicalEarnings[user.id] || 0;
                                                                                                const userAssignment = task.assignedUsers.find(a => {
                                                                                                    const assignee = a.user || a.staff;
                                                                                                    return assignee?._id === user.id;
                                                                                                });
                                                                                                const dataCount = userAssignment?.dataSubmitted?.length || 0;

                                                                                                return (
                                                                                                    <div key={user.id} className="bg-white dark:bg-gray-600 rounded-lg p-3 mb-2">
                                                                                                        <div className="flex items-start justify-between">
                                                                                                            <div className="flex-1">
                                                                                                                <div className="flex items-center mb-2">
                                                                                                                    <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                                                                                                                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                                                                            {user.name?.split(' ').map((n: string) => n[0]).join('') || 'N/A'}
                                                                                                                        </span>
                                                                                                                    </div>
                                                                                                                    <div>
                                                                                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                                                                            {user.name || 'N/A'}
                                                                                                                        </p>
                                                                                                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                                                                            {user.designation ? user.designation.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : 'N/A'}
                                                                                                                        </p>
                                                                                                                    </div>
                                                                                                                </div>

                                                                                                                <div className="ml-11 space-y-1">
                                                                                                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                                                                        <strong>Email:</strong> {user.email || 'N/A'}
                                                                                                                    </p>
                                                                                                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                                                                        <strong>Phone:</strong> {user.phone || 'N/A'}
                                                                                                                    </p>
                                                                                                                </div>
                                                                                                            </div>

                                                                                                            <div className="text-right ml-4">
                                                                                                                {/* <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2 mb-2">
                                                                                                                    <p className="text-xs text-gray-600 dark:text-gray-400">Data Entries</p>
                                                                                                                    <p className="text-lg font-bold text-green-600 dark:text-green-400">{dataCount}</p>
                                                                                                                </div> */}
                                                                                                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 mb-2">
                                                                                                                    <p className="text-xs text-gray-600 dark:text-gray-400">Total Earnings</p>
                                                                                                                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">₹{userEarnings.toLocaleString()}</p>
                                                                                                                </div>

                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-600 dark:text-gray-400">No team assignments found for this task</p>
                                        </div>
                                    )}

                                    {/* Staff Assignments */}
                                    {task?.assignedUsers && task.assignedUsers.filter(a => a.staff).length > 0 && (
                                        <div className="mt-8">
                                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                                <span className="h-3 w-3 bg-orange-500 rounded-full mr-2"></span>
                                                Staff Assignments
                                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                                    ({task.assignedUsers.filter(a => a.staff).length} staff)
                                                </span>
                                            </h4>

                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {task.assignedUsers.filter(a => a.staff).map((assignment) => {
                                                    const staff = assignment.staff;
                                                    const dataCount = assignment.dataSubmitted?.length || 0;
                                                    const hierarchicalEarnings = calculateHierarchicalEarnings();
                                                    const staffEarnings = staff?._id ? hierarchicalEarnings[staff._id] || 0 : 0;

                                                    return (
                                                        <div key={staff?._id} className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                                                            <div className="flex items-center mb-3">
                                                                <div className="h-10 w-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mr-3">
                                                                    <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                                                                        {staff?.firstName?.[0] || 'N'}{staff?.lastName?.[0] || 'A'}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {staff?.firstName || 'N/A'} {staff?.lastName || 'N/A'}
                                                                    </p>
                                                                    <p className="text-xs text-orange-600 dark:text-orange-400">
                                                                        {(staff as any)?.role?.replace('_', ' ').toUpperCase() || 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2">
                                                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                    <strong>Email:</strong> {staff?.email || 'N/A'}
                                                                </p>
                                                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                    <strong>Phone:</strong> {(staff as any)?.phone || 'N/A'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600 dark:text-gray-400">No team hierarchy data available</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'earnings' && (
                        <div className="p-8">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                                <BanknotesIcon className="h-5 w-5 text-green-600 mr-2" />
                                Earnings Distribution
                            </h3>

                            {earningsData ? (
                                <div className="space-y-6">
                                    {/* Earnings Summary */}
                                    {(() => {
                                        // Calculate total data submitted across all users
                                        const totalDataSubmitted = task?.assignedUsers?.reduce((total, assignment) => {
                                            return total + (assignment.dataSubmitted?.length || 0);
                                        }, 0) || 0;

                                        // Calculate distributed earnings using budget scenarios
                                        let distributedEarnings = 0;
                                        for (let i = 1; i <= totalDataSubmitted; i++) {
                                            const applicableScenario = task?.budgetScenarios?.find(scenario =>
                                                scenario.isActive && i >= scenario.range.min && i <= scenario.range.max
                                            );

                                            if (applicableScenario) {
                                                const scenarioTotal = Object.values(applicableScenario.budgetDistribution).reduce((sum, amount) => sum + amount, 0);
                                                distributedEarnings += scenarioTotal;
                                            }
                                        }

                                        return (
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Total Data Submitted</h4>
                                                    <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                                                        {totalDataSubmitted}
                                                    </p>
                                                </div>
                                                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 border border-yellow-200 dark:border-yellow-800">
                                                    <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Total Budget</h4>
                                                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                                        ₹{(task?.totalBudget || 0).toLocaleString()}
                                                    </p>
                                                </div>
                                                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
                                                    <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">Distributed Earnings</h4>
                                                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                                        ₹{distributedEarnings.toLocaleString()}
                                                    </p>
                                                </div>
                                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                                                    <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">Per Data Entry</h4>
                                                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                                        ₹{totalDataSubmitted > 0 ? Math.round(distributedEarnings / totalDataSubmitted).toLocaleString() : '0'}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Earnings by Designation */}
                                    {earningsData.earningsByDesignation && (
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Earnings by Designation</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {Object.entries(earningsData.earningsByDesignation).map(([designation, data]: [string, any]) => (
                                                    <div key={designation} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                                        <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                                                            {designation.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </h5>
                                                        <div className="space-y-1 text-sm">
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-gray-400">Users:</span>
                                                                <span className="text-gray-900 dark:text-white">{data.userCount}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-gray-400">Total:</span>
                                                                <span className="text-green-600 dark:text-green-400 font-medium">₹{(data.totalEarnings || 0).toLocaleString()}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-gray-400">Verified:</span>
                                                                <span className="text-blue-600 dark:text-blue-400 font-medium">₹{(data.verifiedEarnings || 0).toLocaleString()}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Earnings by Region */}
                                    {earningsData.earningsByRegion && (
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Earnings by Region</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                {Object.entries(earningsData.earningsByRegion).map(([region, data]: [string, any]) => (
                                                    <div key={region} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                                        <h5 className="font-medium text-gray-900 dark:text-white mb-2">{region}</h5>
                                                        <div className="space-y-1 text-sm">
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-gray-400">Users:</span>
                                                                <span className="text-gray-900 dark:text-white">{data.userCount}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-gray-400">Total:</span>
                                                                <span className="text-green-600 dark:text-green-400 font-medium">₹{(data.totalEarnings || 0).toLocaleString()}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <BanknotesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600 dark:text-gray-400">No earnings data available</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'data' && (
                        <div className="p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                    <ChartBarIcon className="h-5 w-5 text-purple-600 mr-2" />
                                    Data Collection & Validation
                                </h3>
                                <button
                                    onClick={() => setShowCsvValidationModal(true)}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                >
                                    <ChartBarIcon className="h-4 w-4" />
                                    Validate Data via CSV
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Data Collection Summary */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                                        <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">Total Data Entries</h4>
                                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                            {filterAssignmentsByHierarchy(task.assignedUsers).reduce((total, assignment) => total + (assignment.dataSubmitted?.length || 0), 0)}
                                        </p>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                            Data entries submitted
                                        </p>
                                    </div>

                                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
                                        <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">Validated Entries</h4>
                                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                            {filterAssignmentsByHierarchy(task.assignedUsers).reduce((total, assignment) =>
                                                total + (assignment.dataSubmitted?.filter((entry: any) => entry.isValidated)?.length || 0), 0)}
                                        </p>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                            Entries validated
                                        </p>
                                    </div>

                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 border border-yellow-200 dark:border-yellow-800">
                                        <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Pending Validation</h4>
                                        <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                            {filterAssignmentsByHierarchy(task.assignedUsers).reduce((total, assignment) =>
                                                total + (assignment.dataSubmitted?.filter((entry: any) => !entry.isValidated)?.length || 0), 0)}
                                        </p>
                                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                            Awaiting validation
                                        </p>
                                    </div>

                                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-800">
                                        <h4 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2">Total Earnings</h4>
                                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                            ₹{(() => {
                                                // Calculate total earnings using budget scenarios
                                                let totalEarnings = 0;
                                                const totalDataSubmitted = task?.assignedUsers?.reduce((total, assignment) => {
                                                    return total + (assignment.dataSubmitted?.length || 0);
                                                }, 0) || 0;

                                                // Calculate earnings for each data entry using budget scenarios
                                                for (let i = 1; i <= totalDataSubmitted; i++) {
                                                    const applicableScenario = task?.budgetScenarios?.find(scenario =>
                                                        scenario.isActive && i >= scenario.range.min && i <= scenario.range.max
                                                    );

                                                    if (applicableScenario) {
                                                        const scenarioTotal = Object.values(applicableScenario.budgetDistribution).reduce((sum, amount) => sum + amount, 0);
                                                        totalEarnings += scenarioTotal;
                                                    }
                                                }

                                                return totalEarnings.toLocaleString();
                                            })()}
                                        </p>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                                            Budget scenario based calculation
                                        </p>
                                    </div>
                                </div>

                                {/* Budget Scenario Information */}
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Budget Scenario & Earnings Calculation</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Task Budget:</p>
                                        <p className="font-semibold text-green-600 dark:text-green-400">
                                            ₹{task.totalBudget?.toLocaleString() || '0'}
                                        </p>
                                    </div>
                                    <div className="mt-4">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current Budget Scenario Distribution:</p>
                                        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                                            {(() => {
                                                const currentScenario = getCurrentBudgetDistribution();
                                                if (!currentScenario) return null;
                                                return (
                                                    <>
                                                        <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded">
                                                            <p className="font-medium text-blue-800 dark:text-blue-200">Team Executive</p>
                                                            <p className="text-blue-600 dark:text-blue-400">₹{currentScenario.teamExecutive}/entry</p>
                                                        </div>
                                                        <div className="bg-green-100 dark:bg-green-900 p-2 rounded">
                                                            <p className="font-medium text-green-800 dark:text-green-200">Team Leader</p>
                                                            <p className="text-green-600 dark:text-green-400">₹{currentScenario.teamLeader}/entry</p>
                                                        </div>
                                                        <div className="bg-yellow-100 dark:bg-yellow-900 p-2 rounded">
                                                            <p className="font-medium text-yellow-800 dark:text-yellow-200">District Head</p>
                                                            <p className="text-yellow-600 dark:text-yellow-400">₹{currentScenario.districtHead}/entry</p>
                                                        </div>
                                                        <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded">
                                                            <p className="font-medium text-purple-800 dark:text-purple-200">State Head</p>
                                                            <p className="text-purple-600 dark:text-purple-400">₹{currentScenario.stateHead}/entry</p>
                                                        </div>
                                                        <div className="bg-indigo-100 dark:bg-indigo-900 p-2 rounded">
                                                            <p className="font-medium text-indigo-800 dark:text-indigo-200">Regional Head</p>
                                                            <p className="text-indigo-600 dark:text-indigo-400">₹{currentScenario.regionalHead}/entry</p>
                                                        </div>
                                                        <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">
                                                            <p className="font-medium text-gray-800 dark:text-gray-200">Admin</p>
                                                            <p className="text-gray-600 dark:text-gray-400">₹{currentScenario.admin}/entry</p>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>

                                    </div>
                                </div>

                                {/* Required Fields */}
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Required Fields</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {task.validationRules?.requiredFields?.map((field, index) => (
                                            <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full text-sm">
                                                {field}
                                            </span>
                                        )) || []}
                                    </div>
                                </div>

                                {/* Submitted Data by Users */}
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                                        Submitted Data by Users ({filterAssignmentsByHierarchy(task.assignedUsers).length} users with data)
                                    </h4>
                                    <div className="space-y-4">
                                        {filterAssignmentsByHierarchy(task.assignedUsers).map((assignment) => {
                                            const assignee = assignment.user || assignment.staff;
                                            const designation = assignee?.designation || (assignment.staff ? (assignee as any)?.role : undefined);
                                            const dataCount = assignment.dataSubmitted?.length || 0;
                                            const calculatedEarnings = calculateEarnings(task, assignment);
                                            const budgetDistribution = getCurrentBudgetDistribution() || {
                                                teamExecutive: 0,
                                                teamLeader: 0,
                                                districtHead: 0,
                                                stateHead: 0,
                                                regionalHead: 0,
                                                admin: 0
                                            };
                                            const budgetPerEntry = Number(budgetDistribution[designation as keyof typeof budgetDistribution] || 0);
                                            const isStaff = !!assignment.staff;

                                            return (
                                                <div key={assignee?._id || `assignment-${assignment.assignedAt}`} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div className="flex items-center">
                                                            <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-4">
                                                                <span className="text-lg font-medium text-blue-600 dark:text-blue-400">
                                                                    {assignee?.firstName?.[0] || 'N'}{assignee?.lastName?.[0] || 'A'}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-gray-900 dark:text-white">
                                                                    {assignee?.firstName || 'N/A'} {assignee?.lastName || 'N/A'}
                                                                </p>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                    {assignee?.email || 'N/A'}
                                                                </p>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                    {(assignee as any)?.phone || 'N/A'}
                                                                </p>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                    {isStaff
                                                                        ? `${(assignee as any)?.role?.replace('_', ' ').toUpperCase() || 'N/A'} • ${assignee?.designation || 'N/A'}`
                                                                        : `${assignee?.designation?.replace('_', ' ') || 'N/A'} • ${(assignee as any)?.region || 'N/A'}`
                                                                    }
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">Data Entries</p>
                                                            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{dataCount}</p>
                                                            {/* <p className="text-sm text-gray-600 dark:text-gray-400">Base rate per entry</p> */}
                                                            {/* <p className="text-lg font-semibold text-green-600 dark:text-green-400">₹{budgetPerEntry.toLocaleString()}</p> */}

                                                            {/* Earnings Breakdown */}
                                                            <div className="mt-2 space-y-1">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400">Total:</span>
                                                                    <span className="text-sm font-bold text-green-600 dark:text-green-400">₹{calculatedEarnings.totalEarned.toLocaleString()}</span>
                                                                </div>
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-green-600 dark:text-green-400">Validated:</span>
                                                                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">₹{calculatedEarnings.validatedEarnings.toLocaleString()}</span>
                                                                </div>
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-yellow-600 dark:text-yellow-400">Pending:</span>
                                                                    <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">₹{calculatedEarnings.pendingEarnings.toLocaleString()}</span>
                                                                </div>
                                                            </div>

                                                            {designation !== 'team_executive' && (
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                    *Includes hierarchical bonuses
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Data Entries Table */}
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                            <thead className="bg-gray-50 dark:bg-gray-700">
                                                                <tr>
                                                                    {task.validationRules?.requiredFields?.map((field, index) => (
                                                                        <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                            {field}
                                                                        </th>
                                                                    )) || []}
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                        Submitted At
                                                                    </th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                        Status
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                                {assignment.dataSubmitted
                                                                    ?.sort((a: any, b: any) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
                                                                    ?.map((entry: any, index: number) => (
                                                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                                            {task.validationRules?.requiredFields?.map((field, fieldIndex) => (
                                                                                <td key={fieldIndex} className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                                    {entry.data?.[field] || '-'}
                                                                                </td>
                                                                            )) || []}
                                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                                                {entry.submittedAt ? new Date(entry.submittedAt).toLocaleString() : '-'}
                                                                            </td>
                                                                            <td className="px-4 py-2 whitespace-nowrap">
                                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${entry.isValidated
                                                                                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                                                                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                                                                                    }`}>
                                                                                    {entry.isValidated ? 'Validated' : 'Pending'}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    )) || []}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Earnings Breakdown */}
                                                    {task.budgetScenarios && task.budgetScenarios.length > 0 && (
                                                        <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                                                            <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                                                                <ChartBarIcon className="h-4 w-4 text-blue-600 mr-2" />
                                                                Earnings Breakdown by Data Status
                                                            </h5>

                                                            {/* Individual Data Entries */}
                                                            {assignment.dataSubmitted && assignment.dataSubmitted.length > 0 && (
                                                                <div className="mb-4">
                                                                    <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Individual Data Entries:</h6>
                                                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                                                        {assignment.dataSubmitted.map((entry: any, index: number) => {
                                                                            const dataIndex = index + 1;
                                                                            const applicableScenario = task.budgetScenarios
                                                                                .filter((s: any) => s.isActive)
                                                                                .sort((a: any, b: any) => a.range.min - b.range.min)
                                                                                .find((scenario: any) =>
                                                                                    dataIndex >= scenario.range.min &&
                                                                                    dataIndex <= scenario.range.max
                                                                                );

                                                                            if (!applicableScenario) return null;

                                                                            const designationMap: { [key: string]: string } = {
                                                                                'team_executive': 'teamExecutive',
                                                                                'team_leader': 'teamLeader',
                                                                                'district_head': 'districtHead',
                                                                                'state_head': 'stateHead',
                                                                                'regional_head': 'regionalHead',
                                                                                'admin': 'admin',
                                                                                'bdm': 'admin',
                                                                                'project_trainer': 'admin',
                                                                                'recruiter': 'admin',
                                                                                'crm': 'admin'
                                                                            };

                                                                            const assignee = assignment.user || assignment.staff;
                                                                            const designation = assignee?.designation || (assignment.staff ? (assignee as any)?.role : undefined);
                                                                            const budgetKey = designationMap[designation] || designation;
                                                                            const earningsPerEntry = (applicableScenario.budgetDistribution as any)[budgetKey] || 0;
                                                                            const isValidated = entry.isValidated === true;

                                                                            return (
                                                                                <div key={index} className="flex justify-between items-center p-1 bg-white dark:bg-gray-800 rounded text-xs">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span className="font-medium">Entry #{dataIndex}</span>
                                                                                        <span className={`px-1 py-0.5 rounded text-xs ${isValidated
                                                                                            ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                                                                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                                                                                            }`}>
                                                                                            {isValidated ? 'Validated' : 'Pending'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="text-right">
                                                                                        <span className="font-semibold">₹{earningsPerEntry.toLocaleString()}</span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Summary */}
                                                            <div className="grid grid-cols-3 gap-2 text-xs">
                                                                <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                                    <p className="font-medium text-gray-900 dark:text-white">Total</p>
                                                                    <p className="text-green-600 dark:text-green-400 font-bold">₹{calculatedEarnings.totalEarned.toLocaleString()}</p>
                                                                </div>
                                                                <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                                    <p className="font-medium text-gray-900 dark:text-white">Validated</p>
                                                                    <p className="text-green-600 dark:text-green-400 font-bold">₹{calculatedEarnings.validatedEarnings.toLocaleString()}</p>
                                                                </div>
                                                                <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                                    <p className="font-medium text-gray-900 dark:text-white">Pending</p>
                                                                    <p className="text-yellow-600 dark:text-yellow-400 font-bold">₹{calculatedEarnings.pendingEarnings.toLocaleString()}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'applications' && (
                        <div className="p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                    <UserGroupIcon className="h-5 w-5 text-blue-600 mr-2" />
                                    Task Applications
                                </h3>
                                <div className="flex gap-2">
                                    <button
                                        onClick={fetchApplications}
                                        disabled={applicationsLoading}
                                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                                    >
                                        {applicationsLoading ? 'Refreshing...' : 'Refresh'}
                                    </button>
                                    <button
                                        onClick={() => navigate(`/admin/tasks/${task._id}/applications`)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                    >
                                        <EyeIcon className="h-4 w-4" />
                                        View All Applications
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {/* Application Status Summary */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                                    <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">Total Applications</h4>
                                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                        {applicationStatistics.totalApplications}
                                    </p>
                                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                        Applications submitted
                                    </p>
                                </div>

                                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 border border-yellow-200 dark:border-yellow-800">
                                    <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Pending Review</h4>
                                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                        {applicationStatistics.totalApplied}
                                    </p>
                                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                        Awaiting review
                                    </p>
                                </div>

                                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
                                    <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">Approved</h4>
                                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                        {applicationStatistics.totalApproved}
                                    </p>
                                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                        Applications approved
                                    </p>
                                </div>

                                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-800">
                                    <h4 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">Rejected</h4>
                                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                        {applicationStatistics.totalRejected}
                                    </p>
                                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                                        Applications rejected
                                    </p>
                                </div>
                            </div>

                            {/* Recent Applications */}
                            <div className="mt-8">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Applications</h4>
                                {applicationsLoading ? (
                                    <div className="text-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                        <p className="text-gray-600 dark:text-gray-400">Loading applications...</p>
                                    </div>
                                ) : applications.length > 0 ? (
                                    <div className="space-y-3">
                                        {applications.slice(0, 5).map((application) => {
                                            const assignee = application.user || application.staff;
                                            const isStaff = !!application.staff;

                                            return (
                                                <div key={application._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                                    <div className="flex items-center">
                                                        <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                                                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                                                {assignee?.firstName?.[0] || 'N'}{assignee?.lastName?.[0] || 'A'}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">
                                                                {assignee?.firstName || 'N/A'} {assignee?.lastName || 'N/A'}
                                                            </p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                {assignee?.email || 'N/A'}
                                                            </p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                {(assignee as any)?.phone || 'N/A'}
                                                            </p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                {isStaff ? ((assignee as any)?.role?.replace('_', ' ').toUpperCase() || 'N/A') : ((assignee as any)?.region || 'N/A')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${application.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200' :
                                                            application.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200' :
                                                                application.status === 'applied' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                                                                    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                                            }`}>
                                                            {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                                                        </span>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                            {new Date(application.createdAt).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-600 dark:text-gray-400">No applications found for this task</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Invite Users Modal */}
            {showInviteModal && task && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Invite Users to Task
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {task.title}
                                </p>
                                <div className="mt-2">
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Required Skills: </span>
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                        {task.requiredSkills?.map(skill => skill.name).join(', ')}
                                    </span>
                                </div>
                                <div className="mt-1">
                                    <span className="text-xs font-medium text-green-600 dark:text-green-400">Regions: </span>
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                        {task.regions?.join(', ')}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowInviteModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                            {usersLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-gray-600 dark:text-gray-400">Loading available users...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Quick Actions */}
                                    <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                                                    Quick Invite All Matching Users
                                                </h4>
                                                <p className="text-xs text-green-700 dark:text-green-300">
                                                    Automatically invite all users with matching skills and location criteria
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleInviteAllUsers}
                                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
                                            >
                                                <UserGroupIcon className="h-4 w-4" />
                                                Invite All ({availableUsers.length})
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Manual Selection ({selectedUsers.length} selected)
                                        </h4>
                                        <div className="space-y-3">
                                            {availableUsers.map(user => {
                                                const isSelected = selectedUsers.includes(user._id);
                                                return (
                                                    <div
                                                        key={user._id}
                                                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${isSelected
                                                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                                                            : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                                                            }`}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                setSelectedUsers(prev => prev.filter(id => id !== user._id));
                                                            } else {
                                                                setSelectedUsers(prev => [...prev, user._id]);
                                                            }
                                                        }}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                                                    <UserIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                                                </div>
                                                                <div>
                                                                    <h5 className="font-medium text-gray-900 dark:text-white">
                                                                        {user.firstName} {user.lastName}
                                                                    </h5>
                                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                        {user.email}
                                                                    </p>
                                                                    <div className="flex items-center space-x-4 mt-1">
                                                                        <span className="text-xs text-blue-600 dark:text-blue-400">
                                                                            {user.skills?.map((skill: any) => skill.name).join(', ') || 'No skills'}
                                                                        </span>
                                                                        <span className="text-xs text-green-600 dark:text-green-400">
                                                                            {user.region}
                                                                        </span>

                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center space-x-2">
                                                                {isSelected && (
                                                                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                                                        <CheckCircleIcon className="h-3 w-3 text-white" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setShowInviteModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleInviteAllUsers}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                            >
                                Invite All Matching Users
                            </button>
                            <button
                                onClick={handleInviteUsers}
                                disabled={selectedUsers.length === 0}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
                            >
                                Invite {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CSV Validation Modal */}
            {showCsvValidationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Validate Data via CSV Upload
                            </h3>
                            <button
                                onClick={() => {
                                    setShowCsvValidationModal(false);
                                    setCsvFile(null);
                                    setCsvData([]);
                                    setCsvColumns([]);
                                    setColumnMapping({});
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* CSV Upload Section */}
                            <div>
                                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                                    Upload CSV File
                                </h4>
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={handleCsvFileUpload}
                                    className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-200"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                    Upload a CSV file with data to validate against submitted entries. The system will match columns and validate matching entries.
                                </p>
                            </div>

                            {/* Column Mapping Section */}
                            {csvColumns.length > 0 && task && (
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                                        Map CSV Columns to Task Fields
                                    </h4>
                                    <div className="space-y-3">
                                        {csvColumns.map(column => (
                                            <div key={column} className="flex items-center gap-4">
                                                <div className="flex-1">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        CSV Column: {column}
                                                    </label>
                                                    <select
                                                        value={columnMapping[column] || ''}
                                                        onChange={(e) => handleColumnMapping(column, e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    >
                                                        <option value="">Select task field...</option>
                                                        {task.validationRules?.requiredFields?.map(field => (
                                                            <option key={field} value={field}>{field}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {columnMapping[column] && (
                                                    <div className="flex items-center text-green-600 dark:text-green-400">
                                                        <CheckCircleIcon className="h-5 w-5" />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* CSV Preview */}
                            {csvData.length > 0 && (
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                                        CSV Preview ({csvData.length} rows)
                                    </h4>
                                    <div className="overflow-x-auto max-h-60 overflow-y-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-50 dark:bg-gray-700">
                                                <tr>
                                                    {csvColumns.map(column => (
                                                        <th key={column} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                            {column}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                {csvData.slice(0, 5).map((row, index) => (
                                                    <tr key={index}>
                                                        {csvColumns.map(column => (
                                                            <td key={column} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                {row[column]}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {csvData.length > 5 && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                Showing first 5 rows of {csvData.length} total rows
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowCsvValidationModal(false);
                                        setCsvFile(null);
                                        setCsvData([]);
                                        setCsvColumns([]);
                                        setColumnMapping({});
                                    }}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={validateData}
                                    disabled={validationLoading || csvData.length === 0 || Object.keys(columnMapping).length === 0}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {validationLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Validating...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircleIcon className="h-4 w-4" />
                                            Validate Data
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskDetailsPage;
